import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CheckCircle2,
  Info,
  AlertTriangle,
  XCircle,
  Sparkles,
  X,
} from "lucide-react";

/**
 * ToastProvider
 * ---------------------------------------------------------
 * <ToastProvider position="top-center" max={4}>
 *   <App />
 * </ToastProvider>
 *
 * const { toast } = useToast();
 * toast({ title: "Saved", description: "Your changes are live.", status: "success" });
 */

const ToastCtx = createContext(null);

export function ToastProvider({ children, position = "top-right", max = 4 }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, state: "leaving" } : t))
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const clear = useCallback(() => {
    setToasts((prev) => prev.map((t) => ({ ...t, state: "leaving" })));
    setTimeout(() => setToasts([]), 300);
  }, []);

  const push = useCallback(
    (opts) => {
      const id = opts.id ?? Math.random().toString(36).slice(2);
      const next = {
        id,
        title: opts.title,
        description: opts.description,
        status: opts.status ?? "neutral",
        duration: Math.max(1000, opts.duration ?? 4000),
        action: opts.action,
        dismissible: opts.dismissible ?? true,
        state: "entering",
        createdAt: Date.now(),
      };
      setToasts((prev) => {
        const trimmed = prev.length >= max ? prev.slice(1) : prev;
        return [...trimmed, next];
      });
      // Transition to idle after enter animation
      setTimeout(() => {
        setToasts((prev) =>
          prev.map((t) => (t.id === id && t.state === "entering" ? { ...t, state: "idle" } : t))
        );
      }, 20);
      return id;
    },
    [max]
  );

  // Escape clears the latest toast
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && toasts.length) {
        dismiss(toasts[toasts.length - 1].id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toasts, dismiss]);

  const value = useMemo(
    () => ({ toast: push, dismiss, clear }),
    [push, dismiss, clear]
  );

  const posClass = {
    "top-right": "top-4 right-4 items-end",
    "top-left": "top-4 left-4 items-start",
    "bottom-right": "bottom-4 right-4 items-end",
    "bottom-left": "bottom-4 left-4 items-start",
    "top-center": "top-4 left-1/2 -translate-x-1/2 items-center",
    "bottom-center": "bottom-4 left-1/2 -translate-x-1/2 items-center",
  }[position];

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div
        className={`fixed z-[1000] ${posClass} flex flex-col gap-2 pointer-events-none`}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

/* ---------------------------------------------------------
 * ToastItem
 * -------------------------------------------------------*/

const tone = {
  success: {
    Icon: CheckCircle2,
    bar: "bg-success",
    chipBg: "bg-success/10",
    chipText: "text-success",
    borderL: "border-l-success",
  },
  info: {
    Icon: Info,
    bar: "bg-info",
    chipBg: "bg-info/10",
    chipText: "text-info",
    borderL: "border-l-info",
  },
  warning: {
    Icon: AlertTriangle,
    bar: "bg-warning",
    chipBg: "bg-warning/10",
    chipText: "text-warning",
    borderL: "border-l-warning",
  },
  destructive: {
    Icon: XCircle,
    bar: "bg-destructive",
    chipBg: "bg-destructive/10",
    chipText: "text-destructive",
    borderL: "border-l-destructive",
  },
  brand: {
    Icon: Sparkles,
    bar: "bg-brand",
    chipBg: "bg-brand/10",
    chipText: "text-brand",
    borderL: "border-l-brand",
  },
  neutral: {
    Icon: Info,
    bar: "bg-muted-foreground",
    chipBg: "bg-muted",
    chipText: "text-foreground",
    borderL: "border-l-border",
  },
};

function ToastItem({ toast, onDismiss }) {
  const t = tone[toast.status] ?? tone.neutral;
  const barRef = useRef(null);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);
  const startRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Start / resume / pause the timer
  useEffect(() => {
    if (paused) {
      // Pause: record how much time has passed
      if (startRef.current) {
        elapsedRef.current += Date.now() - startRef.current;
        startRef.current = null;
      }
      clearTimeout(timerRef.current);
      // Pause the CSS animation
      if (barRef.current) barRef.current.style.animationPlayState = "paused";
      return;
    }

    // Resume / start
    startRef.current = Date.now();
    const remaining = Math.max(0, toast.duration - elapsedRef.current);

    if (remaining <= 0) {
      onDismiss();
      return;
    }

    // Resume the CSS animation
    if (barRef.current) barRef.current.style.animationPlayState = "running";

    timerRef.current = setTimeout(onDismiss, remaining);

    return () => clearTimeout(timerRef.current);
  }, [paused, toast.duration, onDismiss]);

  // Pause on window blur
  useEffect(() => {
    const onBlur = () => setPaused(true);
    const onFocus = () => setPaused(false);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const enterClass = toast.state === "entering"
    ? "opacity-0 translate-y-[-8px]"
    : toast.state === "leaving"
    ? "opacity-0 translate-y-[-8px] scale-95"
    : "opacity-100 translate-y-0 scale-100";

  return (
    <div
      role="status"
      aria-live={toast.status === "destructive" ? "assertive" : "polite"}
      className={[
        "pointer-events-auto surface text-card-foreground border border-border rounded-lg shadow-lg",
        "w-[360px] max-w-[90vw] overflow-hidden",
        "border-l-2",
        t.borderL,
        "transition-all duration-300 ease-out",
        enterClass,
      ].join(" ")}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Content */}
      <div className="p-3.5 flex items-start gap-3">
        <div
          className={[
            "h-8 w-8 grid place-items-center rounded-md shrink-0",
            t.chipBg,
            t.chipText,
          ].join(" ")}
        >
          <t.Icon className="h-4.5 w-4.5" />
        </div>

        <div className="min-w-0 flex-1">
          {toast.title && (
            <div className="text-sm font-medium text-foreground">
              {toast.title}
            </div>
          )}
          {toast.description && (
            <div className="mt-0.5 text-sm leading-snug text-muted-foreground">
              {toast.description}
            </div>
          )}
          {toast.action?.label && (
            <div className="mt-2">
              <button
                type="button"
                className="btn btn-ghost h-8 px-2 text-sm"
                onClick={() => {
                  toast.action?.onClick?.();
                  onDismiss();
                }}
              >
                {toast.action.label}
              </button>
            </div>
          )}
        </div>

        {toast.dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close notification"
            className="btn btn-ghost h-8 w-8 p-0 shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Progress bar — pure CSS animation, no RAF */}
      <div className="h-0.5 w-full bg-muted">
        <div
          ref={barRef}
          className={["h-full", t.bar].join(" ")}
          style={{
            animation: `toast-shrink ${toast.duration}ms linear forwards`,
          }}
        />
      </div>

      <style>{`
        @keyframes toast-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}
